// Команда рабочего пространства: участники и приглашения.
import { apiDelete, apiGet, apiPatch, apiPost } from '../../../shared/api-client/index.js';

export type WorkspaceRole = 'owner' | 'admin' | 'member' | 'viewer';
export type MemberStatus = 'active' | 'suspended' | 'removed';
export type InvitationStatus = 'pending' | 'accepted' | 'revoked';

export interface TeamMember {
  id: string;
  userId: string;
  role: string;
  status: MemberStatus;
  name: string;
  email: string;
}

export interface TeamInvitation {
  id: string;
  email: string;
  role: string;
  status: InvitationStatus;
  expiresAt: string;
  createdAt: string;
  invitedByName: string | null;
}

export interface CreateInvitationInput {
  email: string;
  role: Exclude<WorkspaceRole, 'owner'>;
}

export interface CreateInvitationResult {
  invitation: Pick<TeamInvitation, 'id' | 'email' | 'role' | 'status' | 'expiresAt'>;
  /**
   * Ссылка приходит только в ответе на создание: в базе токена нет, только
   * хэш. Показываем её пригласившему — письмо может не дойти, а почта в
   * окружении может быть вовсе не настроена.
   */
  acceptUrl: string;
}

export interface UpdateMemberInput {
  role?: Exclude<WorkspaceRole, 'owner'>;
  status?: MemberStatus;
}

export interface InvitationPreview {
  workspaceName: string;
  email: string;
  role: string;
  valid: boolean;
  reason?: 'revoked' | 'accepted' | 'expired';
}

export async function listTeamMembers(): Promise<TeamMember[]> {
  return apiGet<TeamMember[]>('/team/members');
}

export async function updateTeamMember(
  memberId: string,
  input: UpdateMemberInput,
): Promise<TeamMember> {
  return apiPatch<TeamMember, UpdateMemberInput>(`/team/members/${memberId}`, input);
}

export interface InvitationsResponse {
  invitations: TeamInvitation[];
  /** Решает сервер: клиент не знает ни про роли, ни про особые пространства. */
  canInvite: boolean;
  reason?: string;
}

export async function listInvitations(): Promise<InvitationsResponse> {
  return apiGet<InvitationsResponse>('/team/invitations');
}

export async function createInvitation(
  input: CreateInvitationInput,
): Promise<CreateInvitationResult> {
  return apiPost<CreateInvitationResult, CreateInvitationInput>('/team/invitations', input);
}

export async function revokeInvitation(invitationId: string): Promise<void> {
  await apiDelete<void>(`/team/invitations/${invitationId}`);
}

/** Просмотр приглашения по ссылке — до входа, поэтому без scope. */
export async function previewInvitation(token: string): Promise<InvitationPreview> {
  return apiGet<InvitationPreview>(`/invitations/preview?token=${encodeURIComponent(token)}`);
}

export async function acceptInvitation(token: string): Promise<{ workspaceId: string }> {
  return apiPost<{ workspaceId: string }, { token: string }>('/invitations/accept', { token });
}
export interface MyPendingInvitation {
  id: string;
  workspaceId: string;
  workspaceName: string;
  role: string;
  email: string;
  expiresAt: string;
  createdAt: string;
}

export async function listMyPendingInvitations(): Promise<MyPendingInvitation[]> {
  const res = await apiGet<{ invitations: MyPendingInvitation[] }>('/invitations/pending');
  return res.invitations;
}

export async function acceptMyInvitation(invitationId: string): Promise<{ workspaceId: string }> {
  return apiPost<{ workspaceId: string }, Record<string, never>>(
    `/invitations/${invitationId}/accept`,
    {},
  );
}
